import React, { useState, useEffect, useRef, memo } from 'react';
import styles from './CoachIntro.module.css';

// Pre-define intro messages to avoid recreating them on every render
const INTRO_MESSAGES = [
    "Hi there! I'm your presentation coach! 👋",
    "I'll help you improve your facial expressions, hand gestures, and posture.",
    "Let's practice! I'll give you feedback as you go."
];

// Timing constants (in ms)
const INTRO_DELAY = 500;
const STEP_DURATION = 2000;

const CoachIntro = ({ isRecording, isFirstRecording, onActiveIntroChange }) => {
    const [visible, setVisible] = useState(false);
    const [introStep, setIntroStep] = useState(0);

    // Store timeouts in a ref to avoid triggering effect re-runs
    const timeoutsRef = useRef([]);

    // Track if component is mounted
    const isMountedRef = useRef(true);

    useEffect(() => {
        // Set mounted flag
        isMountedRef.current = true;

        // Only run this effect if the conditions are right for showing the intro
        if (isRecording && isFirstRecording) {
            // Clear any existing timeouts first
            timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
            timeoutsRef.current = [];

            // Initial delay before showing intro
            const initialDelay = setTimeout(() => {
                if (!isMountedRef.current) return;

                setVisible(true);
                setIntroStep(0);

                // Notify parent that intro is active
                onActiveIntroChange?.(true);

                // Schedule step transitions
                const step1Timeout = setTimeout(() => {
                    if (!isMountedRef.current) return;
                    setIntroStep(1);
                }, STEP_DURATION);

                const step2Timeout = setTimeout(() => {
                    if (!isMountedRef.current) return;
                    setIntroStep(2);
                }, STEP_DURATION * 2);

                const hideTimeout = setTimeout(() => {
                    if (!isMountedRef.current) return;
                    setVisible(false);
                    onActiveIntroChange?.(false);
                }, STEP_DURATION * 3);

                // Store all timeouts
                timeoutsRef.current = [step1Timeout, step2Timeout, hideTimeout];
            }, INTRO_DELAY);

            timeoutsRef.current.push(initialDelay);

            // Cleanup function
            return () => {
                timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
                onActiveIntroChange?.(false);
            };
        } else {
            // If not recording or not first recording, hide intro
            setVisible(false);
            onActiveIntroChange?.(false);

            // Clear any existing timeouts
            timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
            timeoutsRef.current = [];
        }

        // Cleanup function for unmount
        return () => {
            isMountedRef.current = false;
            timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
            onActiveIntroChange?.(false);
        };
    }, [isRecording, isFirstRecording, onActiveIntroChange]);

    // Immediately return null if not visible for better performance
    if (!visible) return null;

    return (
        <div className={styles.introContainer}>
            <div className={styles.introMessage}>
                {INTRO_MESSAGES[introStep]}
            </div>
        </div>
    );
};

// Prevent unnecessary rerenders with memo
export default memo(CoachIntro);